import { useState } from "react";
import { Patient } from "@/components/PatientCard";
import { PatientDetailDrawer } from "@/components/PatientDetailDrawer";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PatientTableRow } from "@/components/PatientTableRow";
import { cn } from "@/lib/utils";
import { ArrowUpDown, Filter, Search } from "lucide-react";
import { usePatients } from "@/hooks/usePatients";
import { Input } from "@/components/ui/input";
import { useHorizontalDrag } from "@/hooks/useHorizontalDrag";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const CATEGORIES = [
  "All Categories",
  "Awaiting Discharge Initiation",
  "Discharge Summary Saved",
  "Discharge Summary Finalized",
  "Charge Sheet",
  "Pharmacy Clearance",
  "Billing",
  "Payment Processing",
  "Physical Discharge",
];

export const PatientListView = () => {
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc" | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>("All Categories");
  const [searchQuery, setSearchQuery] = useState("");
  const scrollRef = useHorizontalDrag();
  
  const { data: patients, isLoading, error } = usePatients();

  const handlePatientClick = (patient: Patient) => {
    setSelectedPatient(patient);
    setIsDrawerOpen(true);
  };

  const toggleSort = () => {
    setSortOrder((prev) => (prev === "asc" ? "desc" : prev === "desc" ? null : "asc"));
  };

  // Filter patients by selected category (DB uses "Billing & Chargesheet", UI shows "Billing")
  const categoryDbValue = selectedCategory === "Billing" ? "Billing & Chargesheet" : selectedCategory;
  const filteredPatients = (patients || []).filter((patient) => {
    if (selectedCategory !== "All Categories" && !patient.categories?.includes(categoryDbValue)) return false;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      const nameMatch = patient.name?.toLowerCase().includes(q);
      const admissionMatch = patient.admissionNumber?.toLowerCase().includes(q);
      const idMatch = patient.id?.toLowerCase().includes(q);
      if (!nameMatch && !admissionMatch && !idMatch) return false;
    }
    return true;
  });

  // Sort patients by admission date
  const sortedPatients = sortOrder
    ? [...filteredPatients].sort((a, b) => {
        const dateA = a.admissionDateTime?.getTime() || 0;
        const dateB = b.admissionDateTime?.getTime() || 0;
        return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
      })
    : filteredPatients;

  if (isLoading) {
    return (
      <div className="w-full px-2 sm:px-3 py-2 sm:py-3">
        <div className="border rounded-lg bg-card overflow-hidden">
          <div className="bg-muted/30 p-3 flex gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-4 w-24" />
            ))}
          </div>
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="flex gap-4 p-3 border-t border-border">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-28" />
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-16" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full h-[calc(100vh-8rem)] flex items-center justify-center">
        <div className="text-destructive">Error loading patient data</div>
      </div>
    );
  }

  return (
    <div className="w-full h-[calc(100vh-11rem)] flex flex-col px-2 sm:px-3 py-2 sm:py-3">
      {/* Filter Section */}
      <div className="flex items-center gap-3 mb-3 flex-shrink-0 w-full flex-wrap">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Filter by:</span>
        </div>
        <Select value={selectedCategory} onValueChange={setSelectedCategory}>
          <SelectTrigger className="w-[240px]">
            <SelectValue placeholder="Select category" />
          </SelectTrigger>
          <SelectContent>
            {CATEGORIES.map((category) => (
              <SelectItem key={category} value={category}>
                {category}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground">
          {sortedPatients.length} patient{sortedPatients.length !== 1 ? "s" : ""}
        </span>
        <div className="ml-auto relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search name, admission #, apex ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-8 h-9 w-[260px] text-sm"
            aria-label="Search patients"
          />
        </div>
      </div>

      {/* Patient Table - vertical and horizontal scrollbars both visible in viewport (same behavior) */}
      <div 
        ref={scrollRef}
        className="border rounded-lg bg-card overflow-y-scroll overflow-x-scroll flex-1 min-h-0"
      >
        {/* w-max so table width is content-based; prevents horizontal scroll body from being empty (w-full would match container and remove overflow) */}
        <Table noScrollWrapper className="min-w-[1600px] w-max">
          <TableHeader>
            <TableRow className="bg-muted/30">
              <TableHead className="text-xs sm:text-sm font-semibold uppercase text-muted-foreground min-w-[200px] sm:w-[229px]">
                Patient Info
              </TableHead>
              <TableHead
                className="text-xs sm:text-sm font-semibold uppercase text-muted-foreground cursor-pointer hover:text-foreground transition-colors min-w-[180px] sm:w-[241px]"
                onClick={toggleSort}
              >
                <div className="flex items-center gap-1">
                  Admission
                  <ArrowUpDown
                    className={cn(
                      "w-4 h-4 transition-transform",
                      sortOrder === "asc" && "text-primary rotate-180",
                      sortOrder === "desc" && "text-primary",
                    )}
                  />
                </div>
              </TableHead>
              <TableHead className="text-xs sm:text-sm font-semibold uppercase text-muted-foreground min-w-[140px] text-center">
                Discharge Initiated
              </TableHead>
              <TableHead className="text-xs sm:text-sm font-semibold uppercase text-muted-foreground min-w-[200px] text-center">
                Current Task
              </TableHead>
              <TableHead className="text-xs sm:text-sm font-semibold uppercase text-muted-foreground min-w-[140px] text-center">
                Discharge Status
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedPatients.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-6 text-base text-muted-foreground">
                  {searchQuery.trim()
                    ? "No patients match your search"
                    : selectedCategory === "All Categories"
                      ? "No patients available"
                      : `No patients in "${selectedCategory}" category`}
                </TableCell>
              </TableRow>
            ) : (
              sortedPatients.map((patient) => (
                <PatientTableRow
                  key={patient.id}
                  patient={patient}
                  onClick={() => handlePatientClick(patient)}
                />
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {selectedPatient && (
        <PatientDetailDrawer patient={selectedPatient} open={isDrawerOpen} onOpenChange={setIsDrawerOpen} />
      )}
    </div>
  );
};
